import { Organization } from "@common/types/organization";
import { FC } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUpdateOrganizationProfileMutation } from "@api/organization.api";
import { HStack } from "@design-system/HStack/HStack";
import { buildProfilePayload } from "./buildProfilePayload";
import styles from "./SettingsPage.module.scss";

const urlField = z
    .string()
    .url("Must be a valid URL")
    .optional()
    .or(z.literal(""));

const schema = z.object({
    calendlyUrl: urlField,
    websiteUrl: urlField,
    writeup: z.string().max(5000).optional().or(z.literal("")),
    videoUrl: urlField,
    googleReviewsUrl: urlField,
    samslistUrl: urlField,
    facebookUrl: urlField,
    yelpUrl: urlField,
    linkedinUrl: urlField,
    twitterUrl: urlField,
    youtubeUrl: urlField,
    idealClientDescription: z.string().max(5000).optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

const fields = createFormikFields<FormValues>();

interface Props {
    organization: Organization;
    onClose(): void;
}

function emptyToUndefined(v: string | undefined): string | undefined {
    return v === "" ? undefined : v;
}

export const EditSellingModal: FC<Props> = ({ organization, onClose }) => {
    const { toast, error } = useToaster();
    const updateProfile = useUpdateOrganizationProfileMutation();
    const profile = organization.profile;

    const formik = useFormik<FormValues>({
        initialValues: {
            calendlyUrl: profile?.calendlyUrl ?? "",
            websiteUrl: profile?.websiteUrl ?? "",
            writeup: profile?.writeup ?? "",
            videoUrl: profile?.videoUrl ?? "",
            googleReviewsUrl: profile?.googleReviewsUrl ?? "",
            samslistUrl: profile?.samslistUrl ?? "",
            facebookUrl: profile?.facebookUrl ?? "",
            yelpUrl: profile?.yelpUrl ?? "",
            linkedinUrl: profile?.linkedinUrl ?? "",
            twitterUrl: profile?.twitterUrl ?? "",
            youtubeUrl: profile?.youtubeUrl ?? "",
            idealClientDescription: profile?.idealClientDescription ?? "",
        },
        validate: validateFormikWithZodSchema(schema),
        onSubmit: submitHelper({
            handler: async (values) => {
                await updateProfile.mutateAsync({
                    organizationId: organization.id,
                    payload: buildProfilePayload(profile, {
                        calendlyUrl: emptyToUndefined(values.calendlyUrl),
                        websiteUrl: emptyToUndefined(values.websiteUrl),
                        writeup: emptyToUndefined(values.writeup),
                        videoUrl: emptyToUndefined(values.videoUrl),
                        googleReviewsUrl: emptyToUndefined(
                            values.googleReviewsUrl,
                        ),
                        samslistUrl: emptyToUndefined(values.samslistUrl),
                        facebookUrl: emptyToUndefined(values.facebookUrl),
                        yelpUrl: emptyToUndefined(values.yelpUrl),
                        linkedinUrl: emptyToUndefined(values.linkedinUrl),
                        twitterUrl: emptyToUndefined(values.twitterUrl),
                        youtubeUrl: emptyToUndefined(values.youtubeUrl),
                        idealClientDescription: emptyToUndefined(
                            values.idealClientDescription,
                        ),
                    }),
                });
                toast("Selling info updated");
                onClose();
            },
            failureHandler: async (err) => {
                // eslint-disable-next-line no-console
                console.error("Failed to save profile", err);
                error("Failed to save profile");
            },
        }),
    });

    return (
        <FormikProvider value={formik}>
            <Form className={styles.settingForm}>
                <fields.Text
                    name="websiteUrl"
                    label="Website URL"
                    size="xl"
                    className={styles.inputField}
                />
                <fields.Text
                    name="calendlyUrl"
                    label="Calendly URL"
                    size="xl"
                    className={styles.inputField}
                />
                <fields.Text
                    name="videoUrl"
                    label="Video URL"
                    size="xl"
                    className={styles.inputField}
                />
                <fields.Text
                    name="googleReviewsUrl"
                    label="Google Reviews URL"
                    size="xl"
                    className={styles.inputField}
                />
                <fields.Text
                    name="samslistUrl"
                    label="Sam's List URL"
                    size="xl"
                    className={styles.inputField}
                />
                <fields.Text
                    name="facebookUrl"
                    label="Facebook URL"
                    size="xl"
                    className={styles.inputField}
                />
                <fields.Text
                    name="yelpUrl"
                    label="Yelp URL"
                    size="xl"
                    className={styles.inputField}
                />
                <fields.Text
                    name="linkedinUrl"
                    label="LinkedIn URL"
                    size="xl"
                    className={styles.inputField}
                />
                <fields.Text
                    name="twitterUrl"
                    label="Twitter/X URL"
                    size="xl"
                    className={styles.inputField}
                />
                <fields.Text
                    name="youtubeUrl"
                    label="YouTube URL"
                    size="xl"
                    className={styles.inputField}
                />
                <fields.TextArea
                    name="writeup"
                    label="Writeup"
                    className={styles.inputField}
                    size="xl"
                    autoGrow
                    minRows={4}
                />
                <fields.TextArea
                    name="idealClientDescription"
                    label="Ideal client description"
                    className={styles.inputField}
                    size="xl"
                    autoGrow
                    minRows={4}
                />

                <HStack as="footer" justify="end">
                    <ButtonWithLoader
                        size="xl"
                        loading={formik.isSubmitting}
                        type="submit"
                        variant="primary"
                    >
                        Save
                    </ButtonWithLoader>
                </HStack>
            </Form>
        </FormikProvider>
    );
};
